import {
  TextInput,
  Textarea,
  SimpleGrid,
  Group,
  Title,
  Button,
  Paper,
  Select,
  Modal,
  Stack,
  Text,
} from "@mantine/core";
import { TransformedValues, useForm } from "@mantine/form";
import { DatePickerInput, DatesProvider } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import {
  IconBuildingHospital,
  IconExclamationCircle,
  IconNumber,
  IconStethoscope,
  IconUser,
} from "@tabler/icons-react";
import { supabase } from "../../../lib/supabaseClient";
import { LinkCloseButton } from "../../../components/LinkButton";
import { useLoaderData, useNavigate } from "react-router";
import { ISurgery } from "../../../types";
import { useState } from "react";
import dayjs from "dayjs";
import { validateAmount, transformAmount, formatAmountForInput } from "./formUtils";

const statusOptions = [
  { label: "Marcada", value: "scheduled" },
  { label: "Pendente", value: "pending" },
  { label: "Paga", value: "paid" },
  { label: "Cancelada", value: "cancelled" },
];

export const EditSurgery = () => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const { editing, surgery } = useLoaderData() as {
    editing: boolean;
    surgery: ISurgery;
  };
  const navigate = useNavigate();

  const form = useForm({
    validateInputOnChange: true,

    initialValues: {
      receipt_id: surgery.receipt_id || "",
      doctor: surgery.doctor || "",
      patient_name: surgery.patient_name || "",
      patient_id: surgery.patient_id || "",
      amount: surgery.amount ? formatAmountForInput(surgery.amount) : "",
      date: surgery.date || dayjs().format("YYYY-MM-DD"),
      hospital: surgery.hospital || "",
      title: surgery.title || "",
      status: surgery.status || "pending",
      notes: surgery.notes || "",
    },
    validate: {
      //receipt_id: (value) => !value || !/([0-9])+/.test(value),
      patient_name: (value) => value.trim().length === 0,
      doctor: (value) => value.trim().length === 0,
      amount: (value) => !!value && validateAmount(value),
    },

    transformValues: (values): ISurgery =>
      Object.entries(values).reduce((acc: ISurgery, [key, val]) => {
        let value = val;
        if (key == "amount") {
          value = transformAmount(val as string);
        }
        return { ...acc, [key]: value || null };
      }, {} as ISurgery),
  });

  const sendForm = async (values: TransformedValues<typeof form>) => {
    if (!Object.keys(values).length) {
      return;
    }

    const { error } = surgery.id
      ? await supabase.from("surgery").update(values).eq("id", surgery.id)
      : await supabase.from("surgery").insert(values);

    if (error) {
      notifications.show({
        message: "Erro salvando dados!",
        icon: <IconExclamationCircle size="1.2rem" />,
        color: "red",
      });
      console.error(error);
    } else {
      return navigate("/app");
    }
  };

  const deleteOrCancel = () => {
    if (!editing) {
      return navigate("/app");
    } else {
      setDeleteModalOpen(true);
    }
  };

  const deleteSurgery = async () => {
    if (!surgery.id) {
      throw new Error(
        "Impossivel deletar cirurgia, identificador não está presente"
      );
    }

    const { error } = await supabase
      .from("surgery")
      .delete()
      .eq("id", surgery.id);

    if (error) {
      notifications.show({
        message: "Erro deletando cirurgia!",
        icon: <IconExclamationCircle size="1.2rem" />,
        color: "red",
      });
    } else {
      return navigate("/app");
    }
  };

  return (
    <Paper radius="md" p="xl" withBorder>
      <form onSubmit={form.onSubmit(sendForm)}>
        <LinkCloseButton pos="absolute" to="/app" />
        <Title order={2} size="h1" fw={600} ta="center">
          {editing ? "Editar cirurgia" : "Nova cirurgia"}
        </Title>

        <SimpleGrid
          cols={{ base: 1, xs: 2 }}
          mt="xl"
          // breakpoints={[{ maxWidth: "sm", cols: 1 }]}
        >
          <TextInput
            label="Número do recibo"
            leftSection={<IconNumber />}
            placeholder="Número do recibo"
            name="receipt_id"
            {...form.getInputProps("receipt_id")}
          />
          <TextInput
            required
            label="Doutor/a"
            leftSection={<IconStethoscope />}
            placeholder="Nome do doutor/a"
            name="doctor"
            {...form.getInputProps("doctor")}
          />
          <TextInput
            required
            label="Nome do Paciente"
            leftSection={<IconUser />}
            placeholder="Nome do paciente"
            name="patient_name"
            {...form.getInputProps("patient_name")}
          />
          <TextInput
            label="CPF do Paciente"
            leftSection={<IconUser />}
            placeholder="XXX.XXX.XXX-XX"
            name="patient_id"
            {...form.getInputProps("patient_id")}
          />
          {/* {os === 'android' || os === 'ios' ?
              <TextInput
                type='date'
                label="Data"
                icon={<IconCalendar/>}
                placeholder="Data da cirurgia"
                name="date"
                {...form.getInputProps('date')}
              />
              : */}
          <DatesProvider settings={{ locale: "pt-BR" }}>
            <DatePickerInput
              required
              label="Data"
              placeholder="Data da cirurgia"
              valueFormat="DD/MM/YYYY"
              {...form.getInputProps("date")}
            />
          </DatesProvider>
          <TextInput
            label="Valor"
            leftSection={"R$"}
            placeholder="0000,00"
            name="amount"
            {...form.getInputProps("amount")}
          />
          <TextInput
            label="Hospital"
            leftSection={<IconBuildingHospital />}
            placeholder="Nome do hospital"
            name="hospital"
            {...form.getInputProps("hospital")}
          />
          <TextInput
            label="Cirurgia"
            placeholder="Tipo da cirurgia"
            name="title"
            {...form.getInputProps("title")}
          />

          <Select
            label="Status da cirurgia"
            placeholder="Selecione um status"
            data={statusOptions}
            name="status"
            {...form.getInputProps("status")}
          />
        </SimpleGrid>

        <Textarea
          mt="md"
          label="Notas"
          placeholder="Notas sobre a cirurgia"
          maxRows={10}
          minRows={5}
          autosize
          name="notes"
          {...form.getInputProps("notes")}
        />

        <Group justify="center" mt="xl">
          <Button type="submit" size="md">
            {editing ? "Salvar" : "Criar"}
          </Button>

          {editing && (
            <Button
              type="button"
              size="md"
              color="red"
              onClick={deleteOrCancel}
            >
              Deletar
            </Button>
          )}
        </Group>
      </form>
      <Modal
        opened={deleteModalOpen}
        onClose={() => {}}
        withCloseButton={false}
      >
        <Stack>
          <Text ta="center" fz="lg">
            Tem certeza que deseja deletar essa cirurgia?
          </Text>
          <Group justify="center">
            <Button
              type="button"
              size="md"
              color="red"
              onClick={deleteSurgery}
              title="Confimar deletar"
            >
              Deletar
            </Button>
            <Button
              type="button"
              size="md"
              onClick={() => setDeleteModalOpen(false)}
              title="Cancelar deletar"
            >
              Não deletar
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
};
